import { observer } from 'mobx-react-lite'
import { Outlet } from 'react-router'
import React, { type ChangeEvent, useRef } from 'react'
import {
  Avatar,
  Box,
  Button,
  Chip,
  ContainerHeader,
  FullPageWrapper,
  Icon,
  type ITabItem,
  showToast,
  Tabs,
  Typography,
} from 'shared/ui'
import { PersonalPath } from 'entities/ExportRouters'
import { EnumUserRoles, roleLabelsMap } from 'entities/Users'
import { AccountProvider, useAccountContext } from '../context/accountContext'
import styles from './styles.module.scss'

const AccountLayoutContent = observer(() => {
  const { user, handleUploadPhoto, openChangeRoleRequestModal, loading } = useAccountContext()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const tabs: ITabItem[] = [
    { name: 'Profile', key: `/${PersonalPath.profile}` },
    { name: 'Security', key: `/${PersonalPath.security}` },
    { name: 'Voicemail drop', key: `/${PersonalPath.voicemailDrop}` },
  ]

  const onUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
    const MAX_SIZE_VALUE_IN_MB = 5
    const MAX_IMAGE_SIZE = MAX_SIZE_VALUE_IN_MB * 1024 * 1024
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
    const file = e.target.files?.[0]

    if (file && !allowedTypes.includes(file.type)) {
      showToast({
        type: 'error',
        title: 'Support format files - jpg, png, or gif',
      })

      return
    }

    if (file && file.size > MAX_IMAGE_SIZE) {
      showToast({
        type: 'error',
        title: `File size larger than ${MAX_SIZE_VALUE_IN_MB} MB`,
      })

      return
    }

    if (file) {
      handleUploadPhoto(file)
    }
  }

  const openFilePicker = () => {
    fileInputRef.current?.value && (fileInputRef.current.value = '')
    fileInputRef.current?.click()
  }

  return (
    <FullPageWrapper className={styles.wrapper}>
      <div className={styles.content}>
        <ContainerHeader
          customTitle={
            <header className={styles.header}>
              <Typography variant='heading-lg' ariaLabel='Profile header'>
                Account
              </Typography>
              <Typography
                className={styles.headerDescription}
                variant='body-md-regular'
                ariaLabel='Profile description'
              >
                Edit your account details
              </Typography>
            </header>
          }
          paddingTop={90}
          paddingBottom={27}
          paddingLeft={4}
          paddingRight={4}
        />

        <div className={styles.profile}>
          <div className={styles.avatarWrap} onClick={openFilePicker}>
            <Avatar size={80} info={user.avatarInfo} />
            <div className={styles.avatarOverlay}>
              <Icon
                icon={'upload'}
                fontSize={20}
                color={'var(--content-primary-primary-inverted)'}
              />
            </div>
          </div>
          <div className={styles.profileInfo}>
            <input
              hidden
              type={'file'}
              ref={fileInputRef}
              accept={'image/jpeg, image/png, image/jpg, image/gif'}
              onChange={onUploadChange}
            />
            <Typography variant='heading-md' ariaLabel='profileFullName'>
              {user.full_name}
            </Typography>
            <Typography
              variant='body-sm-regular'
              ariaLabel='profileEemail'
              color='var(--content-primary-tertiary)'
              tag={'div'}
            >
              {user.email}
            </Typography>

            <Box display={'flex'} alignItems={'center'} paddingTop={'8px'} gap={'8px'}>
              <Chip
                item={{
                  id: user.role,
                  label: roleLabelsMap[user.role],
                }}
                label={roleLabelsMap[user.role]}
                variant={'secondary'}
              />

              {user.role === EnumUserRoles.VIEW_ONLY && (
                <Button
                  text='Request role upgrade'
                  typeBtn='informative'
                  contained='secondary'
                  size={'small'}
                  onClick={openChangeRoleRequestModal}
                  disabled={loading}
                />
              )}
            </Box>
          </div>
        </div>
        <div className={styles.tabsWrap}>
          <Tabs tabs={tabs} isNavigate tabsClassName={styles.tabs} />
          <div className={styles.pageContent}>
            <Outlet />
          </div>
        </div>
      </div>
    </FullPageWrapper>
  )
})

export const AccountLayout = observer(() => {
  return (
    <AccountProvider>
      <AccountLayoutContent />
    </AccountProvider>
  )
})
